/**
 * Hook that uploads staged contract files to S3 via presigned URLs.
 *
 * Flow per file:
 *   1. Call the `createContractUploadUrl` GraphQL mutation to get a
 *      presigned PUT URL + the resulting S3 key.
 *   2. PUT the file bytes directly to S3 (no auth header required —
 *      presigned URLs already encode the auth).
 *   3. Track per-file status so the UI can show progress.
 *
 * The hook intentionally uses `fetch` (not XMLHttpRequest) for the PUT
 * step. Per-file byte progress is a UX nice-to-have, not an AC; the
 * spinner-based "Uploading N files…" message is sufficient for V1. If a
 * later story needs true per-file percentages we can swap to XHR
 * without changing the public hook API.
 *
 * The mutation document is loaded directly via `gql` rather than from
 * `generated/graphql.ts` so this hook can ship before the codegen step
 * runs against the new backend schema.
 * @module features/contracts/hooks/useContractUpload
 */

import { gql, useMutation } from "@apollo/client";
import { uploadAsync, FileSystemUploadType } from "expo-file-system/legacy";
import { useCallback, useState } from "react";

import type { ContractDocumentType, StagedContractFile } from "../types";

/** GraphQL document for the createContractUploadUrl mutation. */
export const CREATE_CONTRACT_UPLOAD_URL_MUTATION = gql`
  mutation CreateContractUploadUrl($input: CreateContractUploadUrlInput!) {
    createContractUploadUrl(input: $input) {
      presignedUrl
      s3Key
    }
  }
`;

/** Per-file upload status surfaced to the UI. */
export type ContractFileUploadStatus =
  | "pending"
  | "uploading"
  | "completed"
  | "failed";

/** Hook arguments. */
export interface UseContractUploadArgs {
  readonly playerId: string;
  readonly documentType: ContractDocumentType;
}

/** Per-file progress entry returned by the hook. */
export interface ContractFileUploadEntry {
  readonly status: ContractFileUploadStatus;
  readonly s3Key?: string;
  readonly errorMessage?: string;
}

/** Public API returned by `useContractUpload`. */
export interface UseContractUploadResult {
  readonly upload: (
    files: readonly StagedContractFile[]
  ) => Promise<readonly StagedContractFile[]>;
  readonly isUploading: boolean;
  readonly progress: Readonly<Record<string, ContractFileUploadEntry>>;
  readonly errorMessage: string | null;
  readonly reset: () => void;
}

/** Shape of the createContractUploadUrl mutation payload. */
interface CreateUploadUrlVariables {
  readonly input: {
    readonly playerId: string;
    readonly fileName: string;
    readonly documentType: ContractDocumentType;
  };
}

/** Shape of the createContractUploadUrl mutation response. */
interface CreateUploadUrlResponse {
  readonly createContractUploadUrl: {
    readonly presignedUrl: string;
    readonly s3Key: string;
  };
}

/** Functional updater used by the hook to mutate per-file progress state. */
type ProgressUpdater = (
  update: (
    prev: Record<string, ContractFileUploadEntry>
  ) => Record<string, ContractFileUploadEntry>
) => void;

/** Apollo mutation function shape used by `uploadSingleFile`. */
type CreateUploadUrlMutationFn = (options: {
  readonly variables: CreateUploadUrlVariables;
}) => Promise<{ readonly data?: CreateUploadUrlResponse | null }>;

/** Arguments passed to `uploadSingleFile`. */
interface UploadSingleFileArgs {
  readonly file: StagedContractFile;
  readonly createUploadUrl: CreateUploadUrlMutationFn;
  readonly playerId: string;
  readonly documentType: ContractDocumentType;
  readonly setProgress: ProgressUpdater;
}

/**
 * Uploads the bytes of a single staged file to its presigned S3 URL,
 * picking the transport that works on the current platform.
 *
 * Web: the picker hands us a real `File`/`Blob` (`file.webFile`), which
 * `fetch` accepts as a request body directly.
 *
 * Native: the picker hands us a `file://` uri. We must NOT round-trip it
 * through `fetch(uri).blob()` — React Native's `fetch` polyfill builds
 * the Blob from an `ArrayBuffer`, and RN's Blob constructor rejects that
 * with "Creating blobs from ArrayBuffer and ArrayBufferView are not
 * supported" (SE-5458; only reproduces on device, works on web). Instead
 * stream the raw bytes straight from disk to S3 via expo-file-system's
 * binary upload.
 * @param file - The staged file to upload
 * @param presignedUrl - The presigned S3 PUT URL for this file
 * @throws {Error} If the file has no readable source or the PUT is not 2xx
 */
async function uploadFileBytes(
  file: StagedContractFile,
  presignedUrl: string
): Promise<void> {
  if (file.webFile) {
    const putResponse = await fetch(presignedUrl, {
      method: "PUT",
      body: file.webFile,
      headers: { "Content-Type": file.type },
    });
    if (!putResponse.ok) {
      throw new Error(
        `Upload failed for ${file.name} (HTTP ${putResponse.status}).`
      );
    }
    return;
  }

  if (file.uri) {
    const result = await uploadAsync(presignedUrl, file.uri, {
      httpMethod: "PUT",
      uploadType: FileSystemUploadType.BINARY_CONTENT,
      headers: { "Content-Type": file.type },
    });
    if (result.status < 200 || result.status >= 300) {
      throw new Error(
        `Upload failed for ${file.name} (HTTP ${result.status}).`
      );
    }
    return;
  }

  throw new Error(`File ${file.name} has no readable source.`);
}

/**
 * Uploads a single staged file via the createContractUploadUrl mutation
 * and a direct PUT to the presigned S3 URL.
 *
 * Extracted to module scope so SonarCloud's 4-level function-nesting
 * limit is respected. Marks the entry as "uploading" on entry and
 * "completed" or "failed" on exit, so the caller always sees an
 * accurate per-file state regardless of where a failure occurred.
 * @param args - Upload parameters
 * @param args.file - The staged file to upload
 * @param args.createUploadUrl - Apollo mutation function
 * @param args.playerId - Player UUID forwarded to the mutation
 * @param args.documentType - Document type forwarded to the mutation
 * @param args.setProgress - State updater for per-file progress entries
 * @throws Error if the mutation response is invalid or the PUT fails
 */
async function uploadSingleFile({
  file,
  createUploadUrl,
  playerId,
  documentType,
  setProgress,
}: UploadSingleFileArgs): Promise<void> {
  setProgress(prev => ({ ...prev, [file.id]: { status: "uploading" } }));

  try {
    const { data } = await createUploadUrl({
      variables: {
        input: { playerId, fileName: file.name, documentType },
      },
    });

    const presigned = data?.createContractUploadUrl?.presignedUrl;
    const s3Key = data?.createContractUploadUrl?.s3Key;
    if (!presigned || !s3Key) {
      throw new Error(`Server did not return an upload URL for ${file.name}.`);
    }

    await uploadFileBytes(file, presigned);

    setProgress(prev => ({
      ...prev,
      [file.id]: { status: "completed", s3Key },
    }));
  } catch (error_) {
    const errorMsg =
      error_ instanceof Error ? error_.message : "Upload failed.";
    setProgress(prev => ({
      ...prev,
      [file.id]: { status: "failed", errorMessage: errorMsg },
    }));
    throw error_;
  }
}

/**
 * Uploads staged contract files to S3 via the createContractUploadUrl
 * mutation + a direct PUT request per file.
 * @param args - Hook arguments
 * @param args.playerId - UUID of the player the documents belong to
 * @param args.documentType - Document type forwarded to the GraphQL mutation
 * @returns Imperative `upload` function plus loading/progress state
 */
export function useContractUpload({
  playerId,
  documentType,
}: UseContractUploadArgs): UseContractUploadResult {
  const [createUploadUrl] = useMutation<
    CreateUploadUrlResponse,
    CreateUploadUrlVariables
  >(CREATE_CONTRACT_UPLOAD_URL_MUTATION);

  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<
    Record<string, ContractFileUploadEntry>
  >({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const reset = useCallback(() => {
    setIsUploading(false);
    setProgress({});
    setErrorMessage(null);
  }, []);

  const upload = useCallback(
    async (
      files: readonly StagedContractFile[]
    ): Promise<readonly StagedContractFile[]> => {
      if (files.length === 0) {
        return [];
      }

      setIsUploading(true);
      setErrorMessage(null);
      setProgress(
        Object.fromEntries(files.map(file => [file.id, { status: "pending" }]))
      );

      // Process files sequentially; build the completed list immutably via
      // reduce so the functional/immutable-data lint rule is satisfied.
      try {
        return await files.reduce<Promise<readonly StagedContractFile[]>>(
          async (previousPromise, file) => {
            const completed = await previousPromise;
            await uploadSingleFile({
              file,
              createUploadUrl,
              playerId,
              documentType,
              setProgress,
            });
            return [...completed, file];
          },
          Promise.resolve([])
        );
      } catch (error_) {
        const message =
          error_ instanceof Error ? error_.message : "Upload failed.";
        setErrorMessage(message);
        throw error_;
      } finally {
        setIsUploading(false);
      }
    },
    [createUploadUrl, playerId, documentType]
  );

  return { upload, isUploading, progress, errorMessage, reset };
}
